import type { NextEditorPlugin, OptionalPlugin } from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import type { DecorationsPlugin } from '@atlaskit/editor-plugin-decorations';
import type { FloatingToolbarPlugin } from '@atlaskit/editor-plugin-floating-toolbar';
import type { SelectionPlugin } from '@atlaskit/editor-plugin-selection';
import type { WidthPlugin } from '@atlaskit/editor-plugin-width';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';

/**
 * Default extension type for Confluence. Products may override this
 * via `AgentManagedExtensionPluginOptions.extensionType`.
 */
export const AGENT_MANAGED_EXTENSION_TYPE_DEFAULT = 'com.atlassian.confluence.macro.core';
export const AGENT_MANAGED_EXTENSION_KEY = 'agent-managed-block';

/**
 * Shape of the `parameters` attribute we attach to the bodied extension.
 */
export type AgentManagedExtensionParameters = {
	/**
	 * Opaque identifier of the AI-managed object that backs this block. The
	 * editor never inspects this id beyond using it to load the config; all
	 * meaning lives server-side.
	 */
	aiManagedObjectId: string;
};

/**
 * Hydrated representation of an AI-managed object loaded from the
 * agent-managed-object backend.
 */
export type AiManagedObject = {
	id: string;
	// TODO: CCINT-7833 - prompt, sources, agent, ...
};

/**
 * Plugin state for the agent-managed-extension plugin.
 */
export type AgentManagedExtensionPluginState = {
	/** Whether the inline configuration popup is currently expanded. */
	isConfigOpen: boolean;
};

/**
 * Plugin options.
 */
export type AgentManagedExtensionPluginOptions = {
	/**
	 * The `extensionType` attribute written into the bodied-extension ADF
	 * node. Each product's macro system uses a different namespace:
	 *
	 * - Confluence: `'com.atlassian.confluence.macro.core'`
	 * - Jira: TBD
	 *
	 * Defaults to `AGENT_MANAGED_EXTENSION_TYPE_DEFAULT` (Confluence).
	 */
	extensionType?: string;
};

export type AgentManagedExtensionPluginActions = {
	/** Close the configure popup (regardless of which block is selected). */
	closeConfig: () => void;
	/** Open the configure popup for the agent-managed block at the selection. */
	openConfig: () => void;
	/** Toggle the configure popup for the agent-managed block at the selection. */
	toggleConfig: () => void;
};

export type AgentManagedExtensionPluginDependencies = [
	OptionalPlugin<AnalyticsPlugin>,
	OptionalPlugin<DecorationsPlugin>,
	OptionalPlugin<FloatingToolbarPlugin>,
	OptionalPlugin<SelectionPlugin>,
	OptionalPlugin<WidthPlugin>,
];

export type AgentManagedExtensionPlugin = NextEditorPlugin<
	'agentManagedExtension',
	{
		actions: AgentManagedExtensionPluginActions;
		dependencies: AgentManagedExtensionPluginDependencies;
		pluginConfiguration: AgentManagedExtensionPluginOptions | undefined;
		sharedState: AgentManagedExtensionPluginState | undefined;
	}
>;

export const pluginKey: PluginKey<AgentManagedExtensionPluginState> = new PluginKey<AgentManagedExtensionPluginState>(
	'agentManagedExtensionPlugin',
);
